import { GlobalStateController } from "bai-react-global-state";
import React from "react";
import { _ } from "js/vendor";

import { ActionPermissionsObject, WorkspacePlanTier } from "common/interfaces";
import { getCanvasBundle } from "js/canvas";
import { appVersion, isPPTAddin } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import { ds } from "js/core/models/dataService";
import { linkedSlides } from "js/core/utilities/conversions";
import * as geom from "js/core/utilities/geom";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { downloadFromUrl, filenameForExport, trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { exportToGoogleSlides } from "js/exporter/exportToGoogleSlides";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog, ShowUpgradeDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import SwitchTemplateDialog from "js/react/views/SwitchTemplate/SwitchTemplateDialog";
import LockedSharedSlideDialog from "js/react/views/TeamResources/dialogs/LockedSharedSlideDialog";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import CreateSharedSlideDialog from "js/react/views/UserOptions/dialogs/CreateSharedSlideDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { uuid } from "common/utils/uuid";

export const CanvasExportType = {
    JPEG: "jpeg",
    PPTX: "pptx",
    GOOGLE: "google"
};

export class FailedToRenderCanvasError extends Error {
}

export class CanvasController extends GlobalStateController {
    constructor(initialState, collaborationSlidesLockService) {
        super({
            ...initialState,
            selectionLayerController: null,
            isCanvasRendered: false,
            isCanvasGenerating: false,
            isCanvasTransitioning: false,
            isCanvasAnimating: false,
            isCanvasInErrorState: false,
            disableUserSelect: false,
            canvasRenderKey: null,
            wrapperRef: React.createRef()
        });
        this._state.canvasController = this;

        this.renderCanvasPromise = null;
        this.collaborationSlidesLockService = collaborationSlidesLockService;

        this._canvasScale = null;
        this._maxCanvasScale = null;

        this._stateDidUpdateCallbacks = [];

        this._canvasBundle = null;

        this._unsubscribeFromWorkspaceStateUpdates = WorkspaceController.onStateDidUpdate(this._handleWorkspaceStateUpdate);

        this._showBranding = !WorkspaceController.actionPermissions[ActionPermissionsObject.REMOVE_BAI_BRANDING].use;

        this.instanceId = uuid();
    }

    get canvas() {
        return this._state.canvas;
    }

    get maxCanvasScale() {
        return this._maxCanvasScale;
    }

    set maxCanvasScale(maxCanvasScale) {
        this._maxCanvasScale = maxCanvasScale;

        if (this.canvas) {
            this.canvas.maxCanvasScale = maxCanvasScale;
        }
    }

    get canvasScale() {
        return this._canvasScale;
    }

    set canvasScale(canvasScale) {
        this._canvasScale = canvasScale;

        if (!this.canvas) {
            // Not rendered/initialized yet
            return;
        }

        if (this.canvas.canvasScale === canvasScale) {
            // Already has correct scale, skip
            return;
        }

        this.canvas.canvasScale = canvasScale;
        if (this.isCanvasRendered && !this.isCanvasGenerating) {
            this.canvas.refreshRender();
        }
    }

    get slide() {
        return this._state.slide;
    }

    get slideId() {
        return this._state.slide.id;
    }

    get isTemplateObsolete() {
        return !!this.canvas?.slideTemplate?.constructor?.updateTemplateId;
    }

    get selectionLayerController() {
        return this._state.selectionLayerController;
    }

    set selectionLayerController(selectionLayerController) {
        // Set silently
        this._state.selectionLayerController = selectionLayerController;

        if (this.canvas) {
            this.canvas.selectionLayerController = selectionLayerController;
        }
    }

    get primaryElement() {
        return this.canvas?.layouter?.canvasElement?.elements?.primary ?? null;
    }

    get canvasScreenBounds() {
        const { canvas } = this._state;
        if (!canvas) {
            return null;
        }

        return geom.Rect.FromBoundingClientRect(canvas.$el[0].getBoundingClientRect());
    }

    get canvasBundle() {
        return this._canvasBundle;
    }

    get canvasRenderKey() {
        return this._state.canvasRenderKey;
    }

    get isCanvasGenerating() {
        return this._state.isCanvasGenerating;
    }

    get isCanvasRendered() {
        return this._state.isCanvasRendered;
    }

    _stateDidUpdate(prevState) {
        this._stateDidUpdateCallbacks.forEach(callback => callback(prevState, this._state));
    }

    _handleWorkspaceStateUpdate = () => {
        const { isCanvasRendered, isCanvasGenerating } = this._state;

        const showBranding = !WorkspaceController.actionPermissions[ActionPermissionsObject.REMOVE_BAI_BRANDING].use;
        if (showBranding !== this._showBranding) {
            this._showBranding = showBranding;
            if (this.canvas) {
                this.canvas.options.showBranding = showBranding;
                if (isCanvasRendered && !isCanvasGenerating) {
                    this.canvas.renderWatermark();
                }
            }
        }
    }

    onStateDidUpdate(callback) {
        this._stateDidUpdateCallbacks.push(callback);
    }

    offStateDidUpdate(callback) {
        this._stateDidUpdateCallbacks.remove(callback);
    }

    async setDisableUserSelect(disableUserSelect) {
        const { disableUserSelect: prevDisableUserSelect } = this._state;
        if (prevDisableUserSelect === disableUserSelect) {
            return;
        }

        await this._updateState({ disableUserSelect });
    }

    async reportCanvasState({ isGenerating, isTransitioning, isRendered, isAnimating, renderKey, isInErrorState }) {
        const stateUpdates = {};

        if (typeof isGenerating === "boolean" && this._state.isCanvasGenerating !== isGenerating) {
            stateUpdates.isCanvasGenerating = isGenerating;
        }
        if (typeof isTransitioning === "boolean" && this._state.isCanvasTransitioning !== isTransitioning) {
            stateUpdates.isCanvasTransitioning = isTransitioning;
        }
        if (typeof isAnimating === "boolean" && this._state.isCanvasAnimating !== isAnimating) {
            stateUpdates.isCanvasAnimating = isAnimating;
        }
        if (typeof renderKey === "string" && this._state.canvasRenderKey !== renderKey) {
            stateUpdates.canvasRenderKey = renderKey;
        }
        if (typeof isRendered === "boolean") {
            // Will always refresh when isRendered is passed
            stateUpdates.isCanvasRendered = isRendered;
        }
        if (typeof isInErrorState === "boolean" && this._state.isCanvasInErrorState !== isInErrorState) {
            stateUpdates.isCanvasInErrorState = isInErrorState;
        }

        if (Object.keys(stateUpdates).length > 0) {
            await this._updateState(stateUpdates);
        }
    }

    async renderCanvas(forceSlideVersion = null, reloadStylesCache = false) {
        const { slide, canvasWidth, canvasHeight, isEditable } = this._state;

        if (!this.renderCanvasPromise) {
            this.renderCanvasPromise = (async () => {
                this._canvasBundle = await getCanvasBundle(forceSlideVersion ?? appVersion);

                // create the slide canvas
                const canvas = new this._canvasBundle.SlideCanvas({
                    dataModel: slide,
                    canvasWidth,
                    canvasHeight,
                    editable: true,
                    showBranding: !WorkspaceController.actionPermissions[ActionPermissionsObject.REMOVE_BAI_BRANDING].use
                });
                canvas.canvasController = this;
                canvas.isEditable = !!isEditable;

                await this._updateState({ canvas, canvasWidth, canvasHeight, isCanvasRendered: false });

                if (this.canvasScale) {
                    canvas.canvasScale = this.canvasScale;
                }
                if (this.maxCanvasScale) {
                    canvas.maxCanvasScale = this.maxCanvasScale;
                }
                if (this.selectionLayerController) {
                    canvas.selectionLayerController = this.selectionLayerController;
                }

                await canvas.renderSlide(reloadStylesCache).catch(err => {
                    const canvasError = new FailedToRenderCanvasError(err.message);
                    canvasError.stack = err.stack;
                    throw canvasError;
                });

                if (this.isTemplateObsolete) {
                    if (canvas.slideTemplate.constructor.updateMigration) {
                        // Migrate model
                        canvas.slideTemplate.constructor.updateMigration(canvas);
                    }

                    // New template id
                    canvas.model.template_id = canvas.slideTemplate.constructor.updateTemplateId;

                    await this.reloadCanvas(null, false, false);

                    return this.canvas;
                }

                return canvas;
            })();
        }

        return await this.renderCanvasPromise;
    }

    refreshRender() {
        this.canvas.layouter.refreshRender();
    }

    async setEditable(isEditable) {
        await this._updateState({ isEditable });

        if (!this.renderCanvasPromise) {
            return;
        }
        await this.renderCanvasPromise;

        this.canvas.isEditable = isEditable;

        await this.canvas.layouter?.generationPromiseChain;

        this.canvas.layouter?.refreshRender();
    }

    async reloadCanvas(forceSlideVersion = null, reloadStylesCache = false, waitForRenderCanvasPromise = true) {
        if (this.renderCanvasPromise && waitForRenderCanvasPromise) {
            await this.renderCanvasPromise.catch(() => {
            }); // Ignore failed render
        }

        const wasCurrentCanvas = this.canvas.isCurrentCanvas;
        if (wasCurrentCanvas) {
            await this.removeAsCurrentCanvas();
        }

        const previousCanvas = this.canvas;

        this.renderCanvasPromise = null;

        try {
            await this.renderCanvas(forceSlideVersion, reloadStylesCache);
        } finally {
            setTimeout(() => {
                previousCanvas.remove();
            }, 500);

            if (wasCurrentCanvas) {
                await this.setAsCurrentCanvas();
            }
        }
    }

    async setAsCurrentCanvas(canEditSharedSlide = false) {
        // Ensure canvas is rendered, ignore rendering errors
        await this.renderCanvas().catch(() => {
        });

        const { canvas, slide } = this._state;

        if (!slide.isLocked() || canEditSharedSlide) {
            app.currentCanvas = canvas;

            canvas.setAsCurrentCanvas();

            await canvas.prepareToShowElements();

            // this is a bit hacky and there may be a better way to handle this
            if (canvas.getPrimaryElement().alwaysShowPropertyPanel) {
                this.shouldHidePropertyPanelWhenLeaving = !PresentationEditorController._state.showPropertyPanel;
                _.delay(() => {
                    // Don't switch to element tab if RevisionPanel or the comments panel is open
                    if (PresentationEditorController._state.selectedPropertyPanelTab !== "versions" && !PresentationEditorController.isCommentsPanelActive) {
                        PresentationEditorController.setSelectedPropertyPanelTab("element");
                    }
                }, 500);
            }
        }
    }

    setOpacity(opacity) {
        return this._updateState({ opacity });
    }

    removeAsCurrentCanvas(awaitFinishedEditing = true) {
        const { canvas } = this._state;
        if (!canvas) {
            return;
        }

        if (app.currentCanvas === canvas) {
            app.currentCanvas = null;
        }

        // Don't auto-hide the panel if the user has since opened the comments panel, it's
        // meant to stay open while navigating between slides
        if (this.shouldHidePropertyPanelWhenLeaving && !PresentationEditorController.isCommentsPanelActive) {
            PresentationEditorController.hidePropertyPanel();
        }

        return canvas.removeAsCurrentCanvas(awaitFinishedEditing);
    }

    /*
       Locks slide for collaborators for lockTimeSeconds
     */
    lockSlideForCollaborators(lockTimeSeconds = 5) {
        if (!this.collaborationSlidesLockService || this.freezeCollaboratorsLock) {
            return;
        }
        this.collaborationSlidesLockService.lock(this.slideId, lockTimeSeconds);
    }

    /*
       Explicitly unlocks slide for collaborators
     */
    unlockSlideForCollaborators() {
        if (!this.collaborationSlidesLockService || this.freezeCollaboratorsLock) {
            return;
        }
        this.collaborationSlidesLockService.unlock(this.slideId);
    }

    /*
      Is slide locked by user (locked for other collaborators)
     */
    isLockedForCollaborators() {
        if (!this.collaborationSlidesLockService) {
            return false;
        }
        const lockState = this.collaborationSlidesLockService.getLockState(this.slideId);
        return !!lockState?.isLockedByMe;
    }

    getTemplate() {
        const { canvas } = this._state;
        return canvas.getTemplate();
    }

    getTemplateName() {
        const { canvas } = this._state;

        const template = this.getTemplate();

        if (canvas.bundleVersion < appVersion) {
            return `${template.title} (v${canvas.bundleVersion})`;
        } else {
            return template.title;
        }
    }

    async editLibrarySlide() {
        const { slide } = this._state;

        if (isPPTAddin) {
            ShowWarningDialog({
                title: "Sorry, this shared slide cannot be edited.",
                message: "Shared slides cannot be edited from PowerPoint."
            });
            return;
        }

        if (WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage) {
            if (await ShowConfirmationDialog({
                title: "This shared slide cannot be edited directly within a presentation.",
                message: <><p>As a librarian in your team, you have permission to edit shared slides.</p><p>Any changes made to this shared slide will automatically be updated in all presentations that use it.</p></>,
                okButtonLabel: "Edit Shared Slide..."
            })) {
                // An extra occurrence of a shared slide has its own id, but the library slide is
                // what holds the content and backs the workspace resource this dialog reads
                ShowDialog(TeamSlideDialog, {
                    slideId: slide.getContentId()
                });
            }
        } else {
            // if the user is a member and the team slide is in their org, let them know they need to contact an owner or librarian
            ShowDialog(LockedSharedSlideDialog);
        }
    }

    switchTemplate = source => {
        const { canvas, slide } = this._state;

        if (slide.isLibrarySlide() && !PresentationEditorController.isSingleSlideEditor) {
            this.editLibrarySlide();
        } else {
            this.lockSlideForCollaborators(30);

            PresentationEditorController.setPopupState(true);
            trackActivity("Slide", "ShowSwitchTemplate", null, null, { source }, { audit: false });
            ShowDialog(SwitchTemplateDialog, {
                canvas,
                onClose: () => {
                    PresentationEditorController.setPopupState(false);
                    this.unlockSlideForCollaborators();
                }
            });
        }
    }

    convertToClassic = async () => {
        const { presentation, canvas } = this._state;

        if (WorkspaceController.actionPermissions[ActionPermissionsObject.CONVERT_TO_CLASSIC].use) {
            this.lockSlideForCollaborators(10);

            await canvas.convertToClassic();
            await PresentationEditorController.setSelectedPropertyPanelTab("element");

            this.unlockSlideForCollaborators();
        } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.CONVERT_TO_CLASSIC, "use")) {
            ShowUpgradeDialog({
                type: UpgradePlanDialogType.UPGRADE_PLAN,
                analytics: { cta: "ConvertToAuthoring", ...presentation.getAnalytics() },
                workspaceId: presentation.getWorkspaceId()
            });
        }
    }

    exportCanvas = ({ type, slide }) => {
        const { presentation } = this._state;
        const workspaceId = presentation.getWorkspaceId();

        switch (type) {
            case CanvasExportType.JPEG:
                this.canvas.dataModel.exportSlideToJpeg();
                break;
            case CanvasExportType.PPTX:
                if (WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use) {
                    const slideIdx = presentation.slides.models.findIndex(s => s.id === slide.id);
                    const slideNum = slideIdx >= 0 ? slideIdx + 1 : null;
                    const fileName = filenameForExport({ name: presentation.get("name"), assetType: "pptx", slideNum });

                    presentation.enqueueExportCache("pptx", url => downloadFromUrl(url, fileName), {
                        title: "Exporting slide to PPTX...",
                        includeSkippedSlides: true,
                        pptxMode: "editable",
                        slideIds: [slide.id],
                    });
                    trackActivity("Presentation", "SlideExport", null, null, { type: "pptx" }, { audit: true });
                } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE, "use")) {
                    ShowDialog(ChangePlanDialog, {
                        featuredPlan: WorkspacePlanTier.PRO,
                        analytics: { cta: "exportToPPT", ...presentation.getAnalytics() },
                    });
                }
                break;
            case CanvasExportType.GOOGLE:
                if (WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use) {
                    exportToGoogleSlides({ includeSkippedSlides: true, slide });
                } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE, "use")) {
                    ShowUpgradeDialog({
                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                        analytics: { cta: "ExportToGoogleSlides", ...presentation.getAnalytics() },
                        workspaceId,
                    });
                }
                break;
        }
    }

    convertToSharedSlide = async () => {
        const { slide } = this._state;

        if (!WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage) {
            if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, "manage")) {
                window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), WorkspaceController.plan.type), "_blank");
                return;
            }

            ShowWarningDialog({
                title: "You don't have permission to share slides",
                message: "Please ask your team owner to upgrade your permissions.",
            });
            return;
        }

        if (slide.isLibrarySlide()) {
            ShowWarningDialog({
                title: "Failed to add slide to library",
                message: "This slide is already part of another library",
            });
            return;
        }

        // check for linked data which may change the dialogs that
        // are shown for the user
        const containsLinkedData = linkedSlides.detect(ds.selection.slide);
        const onBeforeSaveSlide = containsLinkedData
            ? slide => {
                linkedSlides.remove(slide);
                slide.commit();
            }
            : null;

        // handles displaying the Share Dialog - possibly may be called
        // immediately or after showing a warning about linked slides
        const showShareSlideDialog = () => {
            ShowDialog(CreateSharedSlideDialog, {
                slide,
                presentation: slide.presentation,
                onBeforeSaveSlide
            });
        };

        // since there's linked data, show a dialog warning that
        // the linked slides will be unlinked
        if (containsLinkedData) {
            return ShowConfirmationDialog({
                title: "This slide is linked to other parts of your deck.",
                message: "Sharing it with your team will remove those links. Would you like to continue?",
                acceptCallback: showShareSlideDialog,
                cancelCallback: () => {
                }
            });
        }

        // there's nothing to warn about so immediately
        // display the share dialog
        showShareSlideDialog();
    }

    viewSharedSlideProperties = () => {
        const { slide } = this._state;

        // See editLibrarySlide(): the properties live on the library slide, not on this occurrence
        ShowDialog(TeamSlideDialog, {
            slideId: slide.getContentId()
        });
    }

    dispose() {
        this.canvas?.remove();
        this._unsubscribeFromWorkspaceStateUpdates?.();
    }

    setSlideDesignMode = mode => {
        this._updateState({ slideDesignMode: mode });
        this.canvas.isSlideDesignMode = mode;
        this.canvas.refreshCanvas();
    }

    setSlideLayout = themeSlideId => {
        const { slide } = this._state;

        // remove placeholder values that would override settings in tne new
        removePlaceholderOverrides(this.canvas.model.layout.placeholderValues);
        slide.setThemeSlide(themeSlideId);
    }
}

export function removePlaceholderOverrides(placeholderValues) {
    if (placeholderValues) {
        for (let key of Object.keys(placeholderValues)) {
            delete placeholderValues[key].id;
            delete placeholderValues[key].cellColor;
            delete placeholderValues[key].cellPadding;
            delete placeholderValues[key].x;
            delete placeholderValues[key].y;
            delete placeholderValues[key].width;
            delete placeholderValues[key].height;
            delete placeholderValues[key].rules;
        }
    }
    return placeholderValues;
}
